import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Breadcrumb, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Button, 
  Form, 
  Input, 
  Select,
  DatePicker,
  Tabs,
  Badge,
  Modal,
  Timeline,
  Rate,
  Typography,
  Avatar,
  Divider,
  Empty
} from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  CalendarOutlined, 
  SearchOutlined, 
  SyncOutlined,
  FileExcelOutlined,
  BarChartOutlined,
  StarFilled,
  PhoneOutlined,
  MessageOutlined,
  ShopOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CommentOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

interface BindingData {
  key: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  memberLevel: string;
  butlerId: string;
  butlerName: string;
  bindingTime: string;
  bindingMethod: string;
  bindingStatus: string;
  lastInteractionTime: string;
  lastInteractionType: string;
  totalConsumption: number;
  satisfaction: number;
}

// 添加互动记录接口
interface InteractionRecord {
  id: string;
  customerId: string;
  butlerId: string;
  interactionTime: string;
  interactionType: string;
  interactionContent: string;
  result: string;
  satisfaction: number;
  isFollowUp: boolean;
  followUpTime?: string;
  notes?: string;
}

const ButlerBindingDetails: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('1');
  const [selectedBtnsType, setSelectedBtnsType] = useState<string>('all');
  // 添加互动记录弹窗状态
  const [interactionModalVisible, setInteractionModalVisible] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<BindingData | null>(null);
  const [activeInteractionTab, setActiveInteractionTab] = useState<string>('all');
  
  // 模拟数据
  const bindingData: BindingData[] = [
    {
      key: '1',
      customerId: 'C10001',
      customerName: '张三',
      customerPhone: '13800138001',
      memberLevel: '黑钻卡',
      butlerId: 'BT001',
      butlerName: '李管家',
      bindingTime: '2023-05-10 14:30',
      bindingMethod: '前台引导',
      bindingStatus: 'active',
      lastInteractionTime: '2023-06-10 10:15',
      lastInteractionType: '电话沟通',
      totalConsumption: 12000,
      satisfaction: 4.8,
    },
    {
      key: '2',
      customerId: 'C10002',
      customerName: '李四',
      customerPhone: '13800138002',
      memberLevel: '铂金卡',
      butlerId: 'BT002',
      butlerName: '王管家',
      bindingTime: '2023-04-22 09:45',
      bindingMethod: '预订自动绑定',
      bindingStatus: 'active',
      lastInteractionTime: '2023-06-05 16:30',
      lastInteractionType: '短信回访',
      totalConsumption: 8500,
      satisfaction: 4.5,
    },
    {
      key: '3',
      customerId: 'C10003',
      customerName: '王五',
      customerPhone: '13800138003',
      memberLevel: '金卡',
      butlerId: 'BT001',
      butlerName: '李管家',
      bindingTime: '2023-04-15 11:20',
      bindingMethod: '客人主动要求',
      bindingStatus: 'inactive',
      lastInteractionTime: '2023-05-20 14:45',
      lastInteractionType: '现场服务',
      totalConsumption: 5200,
      satisfaction: 4.2,
    },
    {
      key: '4',
      customerId: 'C10004',
      customerName: '赵六',
      customerPhone: '13800138004',
      memberLevel: '银卡',
      butlerId: 'BT003',
      butlerName: '张管家',
      bindingTime: '2023-05-28 17:10',
      bindingMethod: '营销活动',
      bindingStatus: 'active',
      lastInteractionTime: '2023-06-15 09:20',
      lastInteractionType: '电话沟通',
      totalConsumption: 3200,
      satisfaction: 4.0,
    },
    {
      key: '5',
      customerId: 'C10005',
      customerName: '钱七',
      customerPhone: '13800138005',
      memberLevel: '普卡',
      butlerId: 'BT002',
      butlerName: '王管家',
      bindingTime: '2023-06-01 10:30',
      bindingMethod: '前台引导',
      bindingStatus: 'active',
      lastInteractionTime: '2023-06-13 11:35',
      lastInteractionType: '短信回访',
      totalConsumption: 1800,
      satisfaction: 3.9,
    },
  ];

  // 添加模拟的互动记录数据
  const interactionRecords: InteractionRecord[] = [
    {
      id: 'INT001',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-10 10:15',
      interactionType: '电话沟通',
      interactionContent: '提前确认客户本周末入住需求，询问是否需要特殊安排',
      result: '客户确认需要婴儿床和儿童洗漱用品',
      satisfaction: 5,
      isFollowUp: true,
      followUpTime: '2023-06-12 14:00',
      notes: '客户表示非常感谢提前询问，期待入住'
    },
    {
      id: 'INT002',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-12 14:30',
      interactionType: '短信回访',
      interactionContent: '发送入住确认短信，并附上天气预报和周边餐厅推荐',
      result: '客户回复感谢并确认抵达时间',
      satisfaction: 5,
      isFollowUp: false,
      notes: '客户对餐厅推荐表示很感兴趣'
    },
    {
      id: 'INT003',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-15 18:00',
      interactionType: '现场服务',
      interactionContent: '协助客户办理入住，并安排行李送至房间',
      result: '完成入住流程并提供房间设施介绍',
      satisfaction: 4,
      isFollowUp: true,
      followUpTime: '2023-06-15 20:30',
      notes: '客户对房间很满意，但对空调温度有小建议'
    },
    {
      id: 'INT004',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-15 20:45',
      interactionType: '礼品赠送',
      interactionContent: '送上酒店特色迎宾水果篮和手写欢迎卡',
      result: '客户非常惊喜，拍照分享到社交媒体',
      satisfaction: 5,
      isFollowUp: false,
      notes: '客户表示这是他住过的最贴心的酒店'
    },
    {
      id: 'INT005',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-16 09:30',
      interactionType: '餐饮服务',
      interactionContent: '早餐时主动询问客户用餐体验并推荐当日特色菜品',
      result: '客户接受推荐并预订了晚餐',
      satisfaction: 5,
      isFollowUp: true,
      followUpTime: '2023-06-16 18:00',
      notes: '客户表示早餐品种丰富，很满意'
    },
    {
      id: 'INT006',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-17 11:00',
      interactionType: '问题处理',
      interactionContent: '接到客户反馈房间WiFi信号较弱的问题',
      result: '安排技术人员检查并更换了信号增强器',
      satisfaction: 4,
      isFollowUp: true,
      followUpTime: '2023-06-17 14:00',
      notes: '问题得到及时解决，客户对处理速度表示满意'
    },
    {
      id: 'INT007',
      customerId: 'C10001',
      butlerId: 'BT001',
      interactionTime: '2023-06-18 10:00',
      interactionType: '退房服务',
      interactionContent: '协助客户办理退房并安排行李寄存',
      result: '顺利完成退房并赠送小纪念品',
      satisfaction: 5,
      isFollowUp: false,
      notes: '客户表示下次还会选择入住并推荐给朋友'
    },
    {
      id: 'INT008',
      customerId: 'C10002',
      butlerId: 'BT002',
      interactionTime: '2023-06-05 16:30',
      interactionType: '短信回访',
      interactionContent: '发送周年入住优惠活动短信',
      result: '客户表示收到并考虑在下月安排入住',
      satisfaction: 4,
      isFollowUp: true,
      followUpTime: '2023-06-20 15:00',
      notes: '客户对会员专属优惠很感兴趣'
    },
    {
      id: 'INT009',
      customerId: 'C10003',
      butlerId: 'BT001',
      interactionTime: '2023-05-20 14:45',
      interactionType: '现场服务',
      interactionContent: '处理客户对房间视野的特殊要求',
      result: '为客户更换了高层海景房',
      satisfaction: 4,
      isFollowUp: false,
      notes: '虽然客户要求较多，但最终满意结果'
    },
    {
      id: 'INT010',
      customerId: 'C10004',
      butlerId: 'BT003',
      interactionTime: '2023-06-15 09:20',
      interactionType: '电话沟通',
      interactionContent: '了解客户对此前住宿的满意度并邀请参加满意度调查',
      result: '客户完成调查并获得积分奖励',
      satisfaction: 4,
      isFollowUp: false,
      notes: '客户提出的建议已记录并传达给相关部门'
    },
    {
      id: 'INT011',
      customerId: 'C10005',
      butlerId: 'BT002',
      interactionTime: '2023-06-13 11:35',
      interactionType: '短信回访',
      interactionContent: '发送生日祝福短信及专属优惠',
      result: '客户回复感谢并预订了生日当周的住宿',
      satisfaction: 5,
      isFollowUp: true,
      followUpTime: '2023-06-25 10:00',
      notes: '客户对生日记得特别感动'
    }
  ];

  // 表格列定义
  const columns: ColumnsType<BindingData> = [
    {
      title: '客户信息',
      key: 'customerInfo',
      fixed: 'left',
      width: 160,
      render: (_, record) => (
        <div>
          <div><b>{record.customerName}</b></div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.customerPhone}</div>
          <div>
            <Tag color={
              record.memberLevel === '黑钻卡' ? 'black' :
              record.memberLevel === '铂金卡' ? 'purple' :
              record.memberLevel === '金卡' ? 'gold' :
              record.memberLevel === '银卡' ? 'silver' : 'blue'
            }>
              {record.memberLevel}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: '管家信息',
      key: 'butlerInfo',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.butlerName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>工号: {record.butlerId}</div>
        </div>
      )
    },
    {
      title: '绑定时间',
      dataIndex: 'bindingTime',
      key: 'bindingTime',
      width: 150,
      sorter: (a, b) => a.bindingTime.localeCompare(b.bindingTime),
    },
    {
      title: '绑定方式',
      dataIndex: 'bindingMethod',
      key: 'bindingMethod',
      width: 120,
      filters: [
        { text: '前台引导', value: '前台引导' },
        { text: '预订自动绑定', value: '预订自动绑定' },
        { text: '客人主动要求', value: '客人主动要求' },
        { text: '营销活动', value: '营销活动' },
      ],
      onFilter: (value, record) => record.bindingMethod === value,
    },
    {
      title: '状态',
      dataIndex: 'bindingStatus',
      key: 'bindingStatus',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'error'}>
          {status === 'active' ? '生效中' : '已解绑'}
        </Tag>
      ),
      filters: [
        { text: '生效中', value: 'active' },
        { text: '已解绑', value: 'inactive' },
      ],
      onFilter: (value, record) => record.bindingStatus === value,
    },
    {
      title: '最近互动',
      children: [
        {
          title: '时间',
          dataIndex: 'lastInteractionTime',
          key: 'lastInteractionTime',
          width: 150,
        },
        {
          title: '类型',
          dataIndex: 'lastInteractionType',
          key: 'lastInteractionType',
          width: 120,
        },
      ]
    },
    {
      title: '消费金额',
      dataIndex: 'totalConsumption',
      key: 'totalConsumption',
      width: 120,
      sorter: (a, b) => a.totalConsumption - b.totalConsumption,
      render: (value: number) => `¥${value.toLocaleString()}`,
    },
    {
      title: '满意度',
      dataIndex: 'satisfaction',
      key: 'satisfaction',
      width: 120,
      sorter: (a, b) => a.satisfaction - b.satisfaction,
      render: (rating: number) => (
        <div>
          <span style={{ color: '#faad14' }}>
            {Array(Math.floor(rating)).fill(0).map((_, index) => (
              <StarFilled key={index} style={{ marginRight: 2 }} />
            ))}
          </span>
          <span style={{ marginLeft: 5 }}>{rating.toFixed(1)}</span>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small">详情</Button>
          {record.bindingStatus === 'active' ? (
            <Button type="link" size="small" danger>解绑</Button>
          ) : (
            <Button type="link" size="small">重新绑定</Button>
          )}
          <Button 
            type="link" 
            size="small" 
            onClick={() => handleViewInteractionRecords(record)}
          >
            互动记录
          </Button>
        </Space>
      ),
    },
  ];

  // 获取汇总数据
  const getActiveBindingCount = () => {
    return bindingData.filter(item => item.bindingStatus === 'active').length;
  };

  const getInactiveBindingCount = () => {
    return bindingData.filter(item => item.bindingStatus === 'inactive').length;
  };

  const getTotalConsumption = () => {
    return bindingData.reduce((sum, item) => sum + item.totalConsumption, 0);
  };

  const getAverageSatisfaction = () => {
    const sum = bindingData.reduce((sum, item) => sum + item.satisfaction, 0);
    return sum / bindingData.length;
  };

  // 按管家分组的绑定数据
  const getBindingsByButler = () => {
    const result: {[key: string]: number} = {};
    bindingData.forEach(item => {
      if (result[item.butlerName]) {
        result[item.butlerName]++;
      } else {
        result[item.butlerName] = 1;
      }
    });
    return Object.entries(result).map(([name, count]) => ({
      name,
      count,
    }));
  };

  // 按绑定方式分组的数据
  const getBindingsByMethod = () => {
    const result: {[key: string]: number} = {};
    bindingData.forEach(item => {
      if (result[item.bindingMethod]) {
        result[item.bindingMethod]++;
      } else {
        result[item.bindingMethod] = 1;
      }
    });
    return Object.entries(result).map(([method, count]) => ({
      method,
      count,
    }));
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleButtonGroupChange = (type: string) => {
    setSelectedBtnsType(type);
  };

  // 根据当前选中的按钮类型获取绑定数据
  const getFilteredBindingData = () => {
    if (selectedBtnsType === 'all') {
      return bindingData;
    } else if (selectedBtnsType === 'active') {
      return bindingData.filter(item => item.bindingStatus === 'active');
    } else if (selectedBtnsType === 'inactive') {
      return bindingData.filter(item => item.bindingStatus === 'inactive');
    }
    return bindingData;
  };

  const butlerBindingColumns: ColumnsType<any> = [
    {
      title: '管家姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '绑定客户数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
  ];

  const bindingMethodColumns: ColumnsType<any> = [
    {
      title: '绑定方式',
      dataIndex: 'method',
      key: 'method',
    },
    {
      title: '数量',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
  ];

  // 添加互动记录相关方法
  const handleViewInteractionRecords = (customer: BindingData) => {
    setSelectedCustomer(customer);
    setInteractionModalVisible(true);
  };

  const handleInteractionModalClose = () => {
    setInteractionModalVisible(false);
    setSelectedCustomer(null);
    setActiveInteractionTab('all');
  };

  // 根据客户ID获取对应的互动记录
  const getCustomerInteractionRecords = (customerId: string) => {
    return interactionRecords.filter(record => record.customerId === customerId);
  };

  // 根据分类筛选互动记录
  const getFilteredInteractionRecords = (customerId: string) => {
    const records = getCustomerInteractionRecords(customerId);
    
    if (activeInteractionTab === 'all') {
      return records;
    } else {
      return records.filter(record => record.interactionType === activeInteractionTab);
    }
  };

  // 获取互动类型统计数据
  const getInteractionTypeCount = (customerId: string) => {
    const records = getCustomerInteractionRecords(customerId);
    const result: {[key: string]: number} = {};
    
    records.forEach(record => {
      if (result[record.interactionType]) {
        result[record.interactionType]++;
      } else {
        result[record.interactionType] = 1;
      }
    });
    
    return result;
  };

  // 获取平均满意度
  const getAverageInteractionSatisfaction = (customerId: string) => {
    const records = getCustomerInteractionRecords(customerId);
    if (records.length === 0) return 0;
    
    const sum = records.reduce((acc, record) => acc + record.satisfaction, 0);
    return sum / records.length;
  };

  // 获取互动类型的图标
  const getInteractionTypeIcon = (type: string) => {
    switch (type) {
      case '电话沟通':
        return <PhoneOutlined style={{ color: '#1890ff' }} />;
      case '短信回访':
        return <MessageOutlined style={{ color: '#52c41a' }} />;
      case '现场服务':
        return <UserOutlined style={{ color: '#faad14' }} />;
      case '问题处理':
        return <CheckCircleOutlined style={{ color: '#ff4d4f' }} />;
      case '礼品赠送':
        return <GiftOutlined style={{ color: '#722ed1' }} />;
      case '餐饮服务':
        return <ShopOutlined style={{ color: '#eb2f96' }} />;
      case '退房服务':
        return <ClockCircleOutlined style={{ color: '#13c2c2' }} />;
      default:
        return <CommentOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  return (
    <div className="butler-binding-details">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '营销管理' },
          { title: '管家绑定明细' },
        ]}
      />

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Form layout="horizontal">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="客户姓名/手机号">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="管家姓名">
                <Select 
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: 'BT001', label: '李管家' },
                    { value: 'BT002', label: '王管家' },
                    { value: 'BT003', label: '张管家' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="绑定状态">
                <Select 
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: 'active', label: '生效中' },
                    { value: 'inactive', label: '已解绑' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="绑定时间">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button icon={<SearchOutlined />} type="primary" style={{ marginRight: 8 }}>
                查询
              </Button>
              <Button icon={<SyncOutlined />}>
                重置
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="绑定总数" 
              value={bindingData.length} 
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="生效中" 
              value={getActiveBindingCount()} 
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="消费总额(元)" 
              value={getTotalConsumption()} 
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="平均满意度" 
              value={getAverageSatisfaction()} 
              precision={1}
              prefix={<StarFilled style={{ color: '#faad14' }} />}
              suffix="/5"
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
      </Row>

      <Card bordered={false}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="绑定明细" key="1">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button 
                  type={selectedBtnsType === 'all' ? 'primary' : 'default'} 
                  onClick={() => handleButtonGroupChange('all')}
                >
                  全部 <Badge count={bindingData.length} style={{ backgroundColor: selectedBtnsType === 'all' ? '#fff' : '#529C8A', color: selectedBtnsType === 'all' ? '#529C8A' : '#fff' }} />
                </Button>
                <Button 
                  type={selectedBtnsType === 'active' ? 'primary' : 'default'} 
                  onClick={() => handleButtonGroupChange('active')}
                >
                  生效中 <Badge count={getActiveBindingCount()} style={{ backgroundColor: selectedBtnsType === 'active' ? '#fff' : '#52c41a', color: selectedBtnsType === 'active' ? '#52c41a' : '#fff' }} />
                </Button>
                <Button 
                  type={selectedBtnsType === 'inactive' ? 'primary' : 'default'} 
                  onClick={() => handleButtonGroupChange('inactive')}
                >
                  已解绑 <Badge count={getInactiveBindingCount()} style={{ backgroundColor: selectedBtnsType === 'inactive' ? '#fff' : '#ff4d4f', color: selectedBtnsType === 'inactive' ? '#ff4d4f' : '#fff' }} />
                </Button>
              </Space>
              <div style={{ float: 'right' }}>
                <Button icon={<FileExcelOutlined />} style={{ marginRight: 8 }}>导出</Button>
                <Button icon={<BarChartOutlined />}>分析</Button>
              </div>
            </div>
            <Table 
              columns={columns} 
              dataSource={getFilteredBindingData()} 
              scroll={{ x: 1300 }}
              pagination={{
                defaultPageSize: 10,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
            />
          </TabPane>
          <TabPane tab="管家分布" key="2">
            <Table 
              columns={butlerBindingColumns}
              dataSource={getBindingsByButler()}
              pagination={false}
            />
          </TabPane>
          <TabPane tab="绑定方式分析" key="3">
            <Table 
              columns={bindingMethodColumns}
              dataSource={getBindingsByMethod()}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 添加互动记录弹窗 */}
      <Modal
        title={
          <div>
            <span>互动记录详情</span>
            {selectedCustomer && (
              <Tag color="blue" style={{ marginLeft: 12 }}>
                {selectedCustomer.customerName} - {selectedCustomer.memberLevel}
              </Tag>
            )}
          </div>
        }
        open={interactionModalVisible}
        onCancel={handleInteractionModalClose}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {selectedCustomer && (
          <>
            {/* 客户和管家信息 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card size="small" title="客户信息">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#529C8A' }} />
                    <div style={{ marginLeft: 16 }}>
                      <Title level={5}>{selectedCustomer.customerName}</Title>
                      <Text type="secondary">{selectedCustomer.customerPhone}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color={
                          selectedCustomer.memberLevel === '黑钻卡' ? 'black' :
                          selectedCustomer.memberLevel === '铂金卡' ? 'purple' :
                          selectedCustomer.memberLevel === '金卡' ? 'gold' :
                          selectedCustomer.memberLevel === '银卡' ? 'silver' : 'blue'
                        }>
                          {selectedCustomer.memberLevel}
                        </Tag>
                        <Tag color="green">累计消费: ¥{selectedCustomer.totalConsumption.toLocaleString()}</Tag>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="管家信息">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar size={64} icon={<TeamOutlined />} style={{ backgroundColor: '#722ed1' }} />
                    <div style={{ marginLeft: 16 }}>
                      <Title level={5}>{selectedCustomer.butlerName}</Title>
                      <Text type="secondary">工号: {selectedCustomer.butlerId}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color="blue">绑定时间: {selectedCustomer.bindingTime}</Tag>
                        <Tag color="cyan">绑定方式: {selectedCustomer.bindingMethod}</Tag>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
            
            {/* 互动统计 */}
            <Card 
              size="small" 
              title="互动概览" 
              style={{ marginBottom: 16 }}
              extra={
                <Rate 
                  disabled 
                  defaultValue={getAverageInteractionSatisfaction(selectedCustomer.customerId)} 
                  allowHalf 
                />
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic 
                    title="互动总次数" 
                    value={getCustomerInteractionRecords(selectedCustomer.customerId).length} 
                    prefix={<CommentOutlined />} 
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="平均满意度" 
                    value={getAverageInteractionSatisfaction(selectedCustomer.customerId).toFixed(1)} 
                    prefix={<StarFilled style={{ color: '#faad14' }} />}
                    suffix="/5"
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="最近互动" 
                    value={selectedCustomer.lastInteractionTime.split(' ')[0]} 
                    prefix={<CalendarOutlined />} 
                  />
                </Col>
              </Row>
              
              <Divider style={{ margin: '12px 0' }} />
              
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                {Object.entries(getInteractionTypeCount(selectedCustomer.customerId)).map(([type, count]) => (
                  <div key={type} style={{ textAlign: 'center', margin: '0 8px' }}>
                    <div>{getInteractionTypeIcon(type)}</div>
                    <div>{type}: {count}次</div>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* 互动记录列表 */}
            <Card 
              size="small" 
              title="互动记录明细"
              tabList={[
                { key: 'all', tab: '全部记录' },
                { key: '电话沟通', tab: '电话沟通' },
                { key: '短信回访', tab: '短信回访' },
                { key: '现场服务', tab: '现场服务' },
                { key: '其他服务', tab: '其他服务' },
              ]}
              activeTabKey={activeInteractionTab}
              onTabChange={setActiveInteractionTab}
            >
              {getFilteredInteractionRecords(selectedCustomer.customerId).length > 0 ? (
                <Timeline mode="left" style={{ marginTop: 20 }}>
                  {getFilteredInteractionRecords(selectedCustomer.customerId)
                    .sort((a, b) => new Date(b.interactionTime).getTime() - new Date(a.interactionTime).getTime())
                    .map(record => (
                      <Timeline.Item 
                        key={record.id}
                        label={record.interactionTime}
                        dot={getInteractionTypeIcon(record.interactionType)}
                        color={record.satisfaction >= 4 ? 'green' : (record.satisfaction >= 3 ? 'orange' : 'red')}
                      >
                        <Card 
                          size="small" 
                          style={{ marginBottom: 8 }}
                          title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{record.interactionType}</span>
                              <Rate disabled defaultValue={record.satisfaction} style={{ fontSize: 12 }} />
                            </div>
                          }
                        >
                          <Paragraph><strong>内容：</strong>{record.interactionContent}</Paragraph>
                          <Paragraph><strong>结果：</strong>{record.result}</Paragraph>
                          {record.notes && <Paragraph><strong>备注：</strong>{record.notes}</Paragraph>}
                          {record.isFollowUp && (
                            <Tag color="blue">需在 {record.followUpTime} 跟进</Tag>
                          )}
                        </Card>
                      </Timeline.Item>
                    ))}
                </Timeline>
              ) : (
                <Empty description="暂无互动记录" />
              )}
            </Card>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ButlerBindingDetails; 